import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Stack, Typography, useMediaQuery } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

// One crumb never takes more than this, so a long site name can't push the trail onto many lines.
const CRUMB_MAX_WIDTH = { xs: 160, sm: 280 };

/**
 * Page title block: optional breadcrumb trail, eyebrow label, title, subtitle
 * and a right-aligned action area. The title keeps room to read (about 320px);
 * when the actions don't fit beside it they move to their own row underneath.
 *
 * - Top-level pages (the nav destinations): no breadcrumbs; the eyebrow is the
 *   area of the app (Overview, Field data, Reference data, Administration, Help).
 * - Record pages and their forms: breadcrumbs from utils/breadcrumbs.js ending
 *   with this page; the eyebrow is the record type (Survey, Transect, Quadrat,
 *   Mature tree…); the subtitle gives the record's own facts on its page, and
 *   what the record is on a create form.
 */
const PageHeader = ({ eyebrow, title, subtitle, breadcrumbs = [], actions, children }) => {
  // On phones the trail fits one line: the page's own crumb is left off (the
  // title says it) and a deep trail keeps its first crumb and the parent, with
  // the rest folded into "…".
  const phone = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const trail = phone && breadcrumbs.length > 1 ? breadcrumbs.slice(0, -1) : breadcrumbs;
  return (
    <Box sx={{ mb: { xs: 3, md: 4 } }}>
      {trail.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
          aria-label="breadcrumb"
          maxItems={phone ? 2 : 8}
          itemsBeforeCollapse={1}
          itemsAfterCollapse={1}
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-ol': { rowGap: 0.5 },
            '& .MuiBreadcrumbs-li': { maxWidth: CRUMB_MAX_WIDTH, minWidth: 0 },
            '& .MuiBreadcrumbs-li > *': {
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        >
          {trail.map((crumb, index) => {
            // The current page is plain text; every crumb above it is a link.
            const isLast = trail === breadcrumbs && index === breadcrumbs.length - 1;
            return isLast || !crumb.to ? (
              <Typography
                key={`${crumb.label}-${index}`}
                variant="body2"
                color={isLast ? 'text.primary' : 'text.secondary'}
                fontWeight={isLast ? 500 : 400}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={`${crumb.label}-${index}`}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                color="text.secondary"
                variant="body2"
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              component="div"
              color="primary.main"
              sx={{ fontWeight: 600, letterSpacing: '0.1em', lineHeight: 1.6 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant="h1" component="h1">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ flex: '0 1 auto', minWidth: 0 }}>
            {actions}
          </Stack>
        )}
      </Box>
      {children}
    </Box>
  );
};

export default PageHeader;
